// SEQ=10115 P8-CK-D — ProgressCardPanel: 본인 진보 카드 목록 + 사용 버튼
// SEQ=10116 P8-CK-E — 카드 라벨을 i18n JSON 모듈로 이관 (`../i18n/progressCards`).
//   인라인 CARD_LABEL는 fallback용으로 남기되, 1차는 i18n lookup.
// EXT_CK ON 룸 전용. CatanGame.isExtCk가 false면 부모가 렌더하지 않음.
import { CatanProgressCard } from '../store/catan'
import { progressName } from '../i18n/progressCards'

interface Props {
  myCards:        CatanProgressCard[]
  isMyTurn:       boolean
  /** 카드 사용 클릭 → 부모가 효과별 payload 입력 모달 또는 자동 전송 */
  onUseClick:     (cardId: number, cardType: string) => void
}

/** 영구 효과 카드 (Constitution/Printer/Defender/Harbormaster) */
const PERMANENT_TYPES = new Set([
  'POL_CONSTITUTION',
  'SCI_PRINTER',
  'POL_DEFENDER_OF_CATAN',
  'TRD_HARBORMASTER',
])

/** 카드 종류 → 한글/영문 라벨 (fallback inline, 1차는 i18n JSON 사용) */
const CARD_LABEL: Record<string, { ko: string; en: string }> = {
  POL_ALCHEMIST:         { ko: '연금술사', en: 'Alchemist' },
  POL_BISHOP:            { ko: '주교',     en: 'Bishop' },
  POL_CONSTITUTION:      { ko: '헌법',     en: 'Constitution' },
  POL_DESERTER:          { ko: '탈주병',   en: 'Deserter' },
  POL_DIPLOMAT:          { ko: '외교관',   en: 'Diplomat' },
  POL_INTRIGUE:          { ko: '음모',     en: 'Intrigue' },
  POL_SABOTEUR:          { ko: '사보타지', en: 'Saboteur' },
  POL_SPY:               { ko: '첩자',     en: 'Spy' },
  POL_WARLORD:           { ko: '군주',     en: 'Warlord' },
  POL_WEDDING:           { ko: '결혼식',   en: 'Wedding' },
  POL_DEFENDER_OF_CATAN: { ko: '카탄의 수호자', en: 'Defender of Catan' },
  SCI_ALCHEMIST:         { ko: '연금술사(과학)', en: 'Alchemist' },
  SCI_CRANE:             { ko: '기중기',   en: 'Crane' },
  SCI_ENGINEER:          { ko: '기술자',   en: 'Engineer' },
  SCI_INVENTOR:          { ko: '발명가',   en: 'Inventor' },
  SCI_IRRIGATION:        { ko: '관개',     en: 'Irrigation' },
  SCI_MEDICINE:          { ko: '의술',     en: 'Medicine' },
  SCI_MINING:            { ko: '광업',     en: 'Mining' },
  SCI_PRINTER:           { ko: '인쇄기',   en: 'Printer' },
  SCI_ROAD_BUILDING:     { ko: '도로 건설', en: 'Road Building' },
  SCI_SMITH:             { ko: '대장장이', en: 'Smith' },
  SCI_MASTER_BUILDER:    { ko: '마스터 빌더', en: 'Master Builder' },
  SCI_RESOURCE_BARTER:   { ko: '자원 교역', en: 'Resource Barter' },
  SCI_PIONEERING:        { ko: '개척',     en: 'Pioneering' },
  SCI_FORGE:             { ko: '대장간',   en: 'Forge' },
  SCI_GUILD:             { ko: '길드',     en: 'Guild' },
  TRD_COMMERCIAL_HARBOR: { ko: '상업 항구', en: 'Commercial Harbor' },
  TRD_MASTER_MERCHANT:   { ko: '대상인',   en: 'Master Merchant' },
  TRD_MERCHANT:          { ko: '상인',     en: 'Merchant' },
  TRD_MERCHANT_FLEET:    { ko: '상선단',   en: 'Merchant Fleet' },
  TRD_RESOURCE_MONOPOLY: { ko: '자원 독점', en: 'Resource Monopoly' },
  TRD_TRADE_MONOPOLY:    { ko: '상품 독점', en: 'Trade Monopoly' },
  TRD_HARBORMASTER:      { ko: '항만청',   en: 'Harbormaster' },
  TRD_TRADE_AGENT:       { ko: '무역 대리인', en: 'Trade Agent' },
  TRD_DEEP_TRADE:        { ko: '심해 무역', en: 'Deep Trade' },
  TRD_CUSTOMS_HOUSE:     { ko: '세관',     en: 'Customs House' },
}

const CATEGORY_COLOR: Record<string, string> = {
  POL: '#aa3333',  // Red
  SCI: '#3355aa',  // Blue
  TRD: '#22aa44',  // Green
}

export default function ProgressCardPanel({ myCards, isMyTurn, onUseClick }: Props) {
  if (myCards.length === 0) {
    return (
      <div className="progress-card-panel" style={{ border: '1px solid #888', padding: 8, marginTop: 8 }}>
        <h4 style={{ margin: '0 0 6px 0' }}>진보 카드 (Progress Cards)</h4>
        <div style={{ fontSize: 12, color: '#666' }}>보유 카드 없음. 자원 다이의 빨간 다이 결과로 추첨됩니다.</div>
      </div>
    )
  }

  return (
    <div className="progress-card-panel" style={{ border: '1px solid #888', padding: 8, marginTop: 8 }}>
      <h4 style={{ margin: '0 0 6px 0' }}>진보 카드 (Progress Cards) — {myCards.length}장</h4>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {myCards.map(card => {
          // SEQ=10116 P8-CK-E — i18n JSON lookup 우선, fallback은 인라인 CARD_LABEL
          const ko = progressName(card.cardType, 'ko')
          const en = progressName(card.cardType, 'en')
          const fallback = CARD_LABEL[card.cardType]
          const showKo = ko !== card.cardType ? ko : (fallback?.ko ?? card.cardType)
          const showEn = en !== card.cardType ? en : (fallback?.en ?? card.cardType)
          const isPerm = PERMANENT_TYPES.has(card.cardType)
          const color  = CATEGORY_COLOR[card.category] ?? '#666'
          return (
            <li key={card.cardId} style={{
              padding: 4, margin: '2px 0',
              border: `2px solid ${color}`, borderRadius: 4,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span title={card.cardType}>
                <strong style={{ color }}>{card.category}</strong>
                {' · '}
                {showKo} <span style={{ color: '#777', fontSize: 11 }}>({showEn})</span>
                {isPerm && <span style={{ marginLeft: 6, color: '#aa6600' }}>★영구</span>}
              </span>
              <button
                onClick={() => onUseClick(card.cardId, card.cardType)}
                disabled={!isMyTurn}
                title={isPerm ? '영구 효과 카드 사용 (게임 종료까지 보드 위 공개)' : '카드 사용 (즉시 효과)'}
                style={{ padding: '2px 8px', fontSize: 12 }}
              >
                사용
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
